import { useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Form, notification } from 'antd'
import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { createCategory, updateCategory } from '@/api/library.api'
import type { LibraryCategory } from '@/types/api'

interface CategoryFormDrawerProps {
  isOpen: boolean
  mode: 'create' | 'update'
  category?: LibraryCategory | null
  onClose: () => void
}

export function CategoryFormDrawer({
  isOpen,
  mode,
  category,
  onClose,
}: CategoryFormDrawerProps) {
  const [form] = Form.useForm<{ name: string }>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (isOpen) {
      form.resetFields()
      if (mode === 'update' && category) {
        form.setFieldsValue({ name: category.name })
      }
    }
  }, [isOpen, mode, category, form])

  const mutation = useMutation({
    mutationFn: (name: string) => {
      if (mode === 'update' && category) {
        return updateCategory(category.id, name)
      }
      return createCategory(name)
    },
    onSuccess: () => {
      notification.success({
        title:
          mode === 'update'
            ? 'Cập nhật thư viện thành công!'
            : 'Thêm mới thư viện thành công!',
      })
      queryClient.invalidateQueries({ queryKey: ['libraryCategories'] })
      onClose()
    },
  })

  const handleSubmit = (values: { name: string }) => {
    mutation.mutate(values.name.trim())
  }

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={mode === 'update' ? 'CẬP NHẬT THƯ VIỆN' : 'THÊM MỚI THƯ VIỆN'}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={mutation.isPending}
            className="min-w-[160px] h-10"
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => form.submit()}
            disabled={mutation.isPending}
            className="min-w-[160px] h-10"
          >
            {mutation.isPending ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DrawerFooter>
      }
    >
      <div className="pt-2">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={(label, info) => (
            <>
              {label}
              {info.required && <span className="text-red-500 ml-1">*</span>}
            </>
          )}
        >
          <Form.Item
            name="name"
            label="Tên thư viện"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Vui lòng nhập tên thư viện',
              },
            ]}
          >
            <Input
              placeholder="Nhập tên thư viện"
              className="rounded-full px-5 h-10 !bg-white"
            />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  )
}
